import { WorkActionCard } from '@/components/work/WorkActionCard';
import { Link, useSearchParams } from 'react-router-dom';
import { getSubjectWorkActions } from '@/lib/work-actions';
import { useState, type FormEvent } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  Clock3,
  MessageCircleQuestion,
  Radar,
  Scale,
  XCircle,
} from 'lucide-react';
import type {
  ActivitySubjectDetail,
  ActivitySubjectRecommendation,
} from '@shared/types/activity-feed';
import type {
  AccountThreadRichView,
  ThreadTopicSummary,
} from '@shared/types/thread-work';
import {
  findTopicRecommendation,
} from './thread-living-model';

const toLocalDateTimeInput = (value: Date): string => {
  const pad = (part: number) => String(part).padStart(2, '0');
  return [
    value.getFullYear(),
    '-',
    pad(value.getMonth() + 1),
    '-',
    pad(value.getDate()),
    'T',
    pad(value.getHours()),
    ':',
    pad(value.getMinutes()),
  ].join('');
};

const defaultReminderInput = (): string => {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1_000);
  date.setSeconds(0, 0);
  return toLocalDateTimeInput(date);
};

const defaultGmailExpiryInput = (): string => {
  const date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1_000);
  date.setSeconds(0, 0);
  return toLocalDateTimeInput(date);
};

const defaultProviderExpiryInput = (
  provider: 'google_calendar' | 'hubspot',
  calendarEndsAt?: string | null
): string => {
  const candidate =
    provider === 'google_calendar' && calendarEndsAt
      ? new Date(new Date(calendarEndsAt).getTime() + 60 * 60 * 1_000)
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1_000);
  const date =
    candidate.getTime() > Date.now()
      ? candidate
      : new Date(Date.now() + 24 * 60 * 60 * 1_000);
  date.setSeconds(0, 0);
  return toLocalDateTimeInput(date);
};

const formatWaitDate = (value: string, timezone: string): string =>
  new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(value));

export const ThreadLivingCard = ({
  detail,
  threadState,
  triagingRecommendationId,
  onTriageRecommendation,
  onAskAlternative,
  onAskWhy,
  onChallenge,
  onAskWait,
  threadChatDecisionFocusEnabled,
  threadWaitTimeEnabled,
  threadWaitGmailEnabled,
  threadWaitCalendarEnabled,
  threadWaitHubSpotEnabled,
  onSnoozeTopic,
  onMonitorGmailReply,
  onMonitorExactProvider,
  onCancelWait,
  onConfirmOutcome,
  sectionTitle = 'Prochaine action',
  resultFocused = false,
}: {
  detail: ActivitySubjectDetail;
  threadState: AccountThreadRichView;
  triagingRecommendationId: string | null;
  onTriageRecommendation: (
    recommendation: ActivitySubjectRecommendation,
    reason: 'already_done' | 'not_relevant'
  ) => void;
  onAskAlternative: (topic: ThreadTopicSummary) => void;
  onAskWhy: (topic: ThreadTopicSummary) => void;
  onChallenge: (topic: ThreadTopicSummary) => void;
  onAskWait: (topic: ThreadTopicSummary) => void;
  threadChatDecisionFocusEnabled: boolean;
  threadWaitTimeEnabled: boolean;
  threadWaitGmailEnabled: boolean;
  threadWaitCalendarEnabled: boolean;
  threadWaitHubSpotEnabled: boolean;
  onSnoozeTopic: (params: {
    topic: ThreadTopicSummary;
    wakeAt: string;
    timezone: string;
    reason: string | null;
  }) => Promise<void>;
  onMonitorGmailReply: (params: {
    topic: ThreadTopicSummary;
    gmailThreadRefId: string;
    expiresAt: string;
    timezone: string;
    reason: string | null;
  }) => Promise<void>;
  onMonitorExactProvider: (params: {
    topic: ThreadTopicSummary;
    provider: 'google_calendar' | 'hubspot';
    providerRefId: string;
    expiresAt: string;
    timezone: string;
    reason: string | null;
  }) => Promise<void>;
  onCancelWait: (params: { topic: ThreadTopicSummary; waitId: string }) => Promise<void>;
  onConfirmOutcome?: (params: { topic: ThreadTopicSummary; evidence: string }) => Promise<void>;
  sectionTitle?: string;
  resultFocused?: boolean;
}) => {
  const [pageParams] = useSearchParams();
  const accountQuery = pageParams.get('q')?.trim();
  const [waitEditorMode, setWaitEditorMode] = useState<
    'time' | 'gmail' | 'google_calendar' | 'hubspot' | null
  >(null);
  const [wakeAtInput, setWakeAtInput] = useState(defaultReminderInput);
  const [snoozeReason, setSnoozeReason] = useState('');
  const [waitMutationPending, setWaitMutationPending] = useState(false);
  const [waitMutationError, setWaitMutationError] = useState<string | null>(null);
  const [outcomeEvidence, setOutcomeEvidence] = useState('');
  const [outcomePending, setOutcomePending] = useState(false);
  const [outcomeError, setOutcomeError] = useState<string | null>(null);
  const topic = threadState.currentTopic;
  const recommendation = findTopicRecommendation(detail.recommendations, topic);
  const work = topic ? getSubjectWorkActions(detail).find(item => item.id === topic.id) : null;
  const activeTimeWait =
    topic?.activeWait?.kind === 'time' && topic.activeWait.status === 'active'
      ? topic.activeWait
      : null;
  const activeGmailWait =
    topic?.activeWait?.kind === 'activity_event' &&
    topic.activeWait.coverage === 'exact' &&
    topic.activeWait.expectedEventType === 'email_received' &&
    topic.activeWait.status === 'active'
      ? topic.activeWait
      : null;
  const activeCalendarWait =
    topic?.activeWait?.kind === 'activity_event' &&
    topic.activeWait.coverage === 'exact' &&
    topic.activeWait.providerKind === 'google_calendar' &&
    topic.activeWait.expectedEventType === 'calendar_event_synced' &&
    topic.activeWait.status === 'active'
      ? topic.activeWait
      : null;
  const activeHubSpotWait =
    topic?.activeWait?.kind === 'activity_event' &&
    topic.activeWait.coverage === 'exact' &&
    topic.activeWait.providerKind === 'hubspot' &&
    topic.activeWait.expectedEventType === 'crm_deal_changed' &&
    topic.activeWait.status === 'active'
      ? topic.activeWait
      : null;
  const activeProviderWait = activeCalendarWait ?? activeHubSpotWait;
  const activeWait = activeTimeWait ?? activeGmailWait ?? activeProviderWait;
  const timezone =
    activeWait?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

  const openSnooze = () => {
    setWakeAtInput(
      activeTimeWait?.wakeAt
        ? toLocalDateTimeInput(new Date(activeTimeWait.wakeAt))
        : defaultReminderInput()
    );
    setSnoozeReason(activeTimeWait?.reason ?? '');
    setWaitMutationError(null);
    setWaitEditorMode('time');
  };

  const openGmailMonitor = () => {
    setWakeAtInput(
      activeGmailWait?.expiresAt
        ? toLocalDateTimeInput(new Date(activeGmailWait.expiresAt))
        : defaultGmailExpiryInput()
    );
    setSnoozeReason(activeGmailWait?.reason ?? '');
    setWaitMutationError(null);
    setWaitEditorMode('gmail');
  };

  const openExactProviderMonitor = (provider: 'google_calendar' | 'hubspot') => {
    const active =
      provider === 'google_calendar' ? activeCalendarWait : activeHubSpotWait;
    setWakeAtInput(
      active?.expiresAt
        ? toLocalDateTimeInput(new Date(active.expiresAt))
        : defaultProviderExpiryInput(provider, topic?.calendarWaitCandidate?.endsAt)
    );
    setSnoozeReason(active?.reason ?? '');
    setWaitMutationError(null);
    setWaitEditorMode(provider);
  };

  const submitWait = async () => {
    if (!topic || !waitEditorMode) return;
    const date = new Date(wakeAtInput);
    if (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now()) {
      setWaitMutationError('Choisissez une date et une heure dans le futur.');
      return;
    }
    setWaitMutationPending(true);
    setWaitMutationError(null);
    try {
      if (waitEditorMode === 'gmail') {
        const gmailThreadRefId =
          activeGmailWait?.providerRefId ?? topic.gmailWaitCandidate?.gmailThreadRefId;
        if (!gmailThreadRefId) {
          throw new Error('Ce thread Gmail n’est plus disponible.');
        }
        await onMonitorGmailReply({
          topic,
          gmailThreadRefId,
          expiresAt: date.toISOString(),
          timezone,
          reason: snoozeReason.trim() || null,
        });
      } else if (waitEditorMode === 'google_calendar' || waitEditorMode === 'hubspot') {
        const providerRefId =
          waitEditorMode === 'google_calendar'
            ? (activeCalendarWait?.providerRefId ??
              topic.calendarWaitCandidate?.googleCalendarEventRefId)
            : (activeHubSpotWait?.providerRefId ??
              topic.hubSpotWaitCandidate?.hubSpotObjectRefId);
        if (!providerRefId) {
          throw new Error('Cette référence provider n’est plus disponible.');
        }
        await onMonitorExactProvider({
          topic,
          provider: waitEditorMode,
          providerRefId,
          expiresAt: date.toISOString(),
          timezone,
          reason: snoozeReason.trim() || null,
        });
      } else {
        await onSnoozeTopic({
          topic,
          wakeAt: date.toISOString(),
          timezone,
          reason: snoozeReason.trim() || null,
        });
      }
      setWaitEditorMode(null);
    } catch (error) {
      setWaitMutationError(
        error instanceof Error ? error.message : 'Impossible de programmer ce rappel.'
      );
    } finally {
      setWaitMutationPending(false);
    }
  };

  const cancelWait = async () => {
    if (!topic || !activeWait) return;
    setWaitMutationPending(true);
    setWaitMutationError(null);
    try {
      await onCancelWait({ topic, waitId: activeWait.id });
      setWaitEditorMode(null);
    } catch (error) {
      setWaitMutationError(
        error instanceof Error ? error.message : 'Impossible d’annuler ce rappel.'
      );
    } finally {
      setWaitMutationPending(false);
    }
  };

  if (!topic) return null;
  const canConfirmOutcome = !resultFocused && Boolean(onConfirmOutcome) &&
    topic.visibleState === 'blocked' && topic.decision?.result === 'block';
  const submitOutcome = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const evidence = outcomeEvidence.trim();
    if (!onConfirmOutcome || evidence.length < 3) {
      setOutcomeError('Indiquez ce qui confirme le résultat.');
      return;
    }
    setOutcomePending(true);
    setOutcomeError(null);
    try {
      await onConfirmOutcome({ topic, evidence });
    } catch (error) {
      setOutcomeError(error instanceof Error ? error.message : 'Impossible de confirmer ce résultat.');
    } finally {
      setOutcomePending(false);
    }
  };
  const hasExpandedOptions = Boolean(
    (threadChatDecisionFocusEnabled && topic.decision) ||
    (threadWaitTimeEnabled && topic.visibleState === 'actionable') ||
    (threadWaitGmailEnabled && (topic.gmailWaitCandidate || activeGmailWait)) ||
    (threadWaitCalendarEnabled && (topic.calendarWaitCandidate || activeCalendarWait)) ||
    (threadWaitHubSpotEnabled && (topic.hubSpotWaitCandidate || activeHubSpotWait)) ||
    activeTimeWait
  );
  const alternativeControl = (
    <button
      type="button"
      onClick={() => onAskAlternative(topic)}
      className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-aubergine transition hover:border-domino-prune hover:bg-domino-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
    >
      <MessageCircleQuestion className="h-4 w-4" />
      Demander une autre suite
    </button>
  );
  return (
    <section className="mb-8" aria-labelledby="thread-current-section-title">
      <h2
        id="thread-current-section-title"
        className="mb-3 text-lg font-semibold text-domino-ink"
      >
        {sectionTitle}
      </h2>
      <div className="overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white">
        {(
          <div className="px-5 py-5 md:px-6 md:py-6">
            {work && <WorkActionCard
              work={work}
              headingId="thread-current-priority-title"
              hideControls={resultFocused || canConfirmOutcome}
            />}

            {canConfirmOutcome && (
              <form onSubmit={(event) => void submitOutcome(event)} className="mt-5 border-t border-domino-cardBorder pt-5">
                <p className="text-sm font-semibold text-domino-ink">Le résultat attendu est-il obtenu ?</p>
                <p className="mt-1 text-sm text-domino-inkSoft">{topic.desiredOutcome}</p>
                <label className="mt-4 block max-w-xl text-sm font-medium text-domino-ink">
                  Ce qui le confirme
                  <input
                    type="text"
                    required
                    minLength={3}
                    maxLength={470}
                    value={outcomeEvidence}
                    onChange={(event) => setOutcomeEvidence(event.target.value)}
                    disabled={outcomePending}
                    placeholder="Ex. Le client a confirmé la prochaine étape"
                    className="mt-2 h-11 w-full rounded-xl border border-domino-cardBorder bg-white px-3 text-sm font-normal text-domino-ink outline-none placeholder:text-domino-inkSoft focus:border-domino-prune focus:ring-2 focus:ring-domino-prune/20"
                  />
                </label>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button type="submit" disabled={outcomePending} className="inline-flex h-10 items-center rounded-xl bg-domino-aubergine px-4 text-sm font-semibold text-white transition hover:bg-domino-prune disabled:opacity-50">
                    {outcomePending ? 'Confirmation…' : 'Terminer ce travail'}
                  </button>
                  {alternativeControl}
                </div>
                {outcomeError && <p role="alert" className="mt-3 text-sm text-red-700">{outcomeError}</p>}
              </form>
            )}

            {!resultFocused && activeTimeWait?.wakeAt && (
              <div className="mt-4 flex items-start gap-2 border-t border-domino-cardBorder pt-4 text-sm text-domino-inkSoft">
                <Clock3 className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Rappel {formatWaitDate(activeTimeWait.wakeAt, activeTimeWait.timezone)}
                </p>
              </div>
            )}

            {!resultFocused && activeGmailWait?.expiresAt && (
              <div className="mt-4 flex items-start gap-2 border-t border-domino-cardBorder pt-4 text-sm text-domino-inkSoft">
                <Radar className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Domino surveille la réponse Gmail jusqu’au{' '}
                  {formatWaitDate(activeGmailWait.expiresAt, activeGmailWait.timezone)}
                </p>
              </div>
            )}

            {!resultFocused && activeCalendarWait?.expiresAt && (
              <div className="mt-4 flex items-start gap-2 border-t border-domino-cardBorder pt-4 text-sm text-domino-inkSoft">
                <Radar className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Domino surveille cet événement Calendar jusqu’au{' '}
                  {formatWaitDate(
                    activeCalendarWait.expiresAt,
                    activeCalendarWait.timezone
                  )}
                </p>
              </div>
            )}

            {!resultFocused && activeHubSpotWait?.expiresAt && (
              <div className="mt-4 flex items-start gap-2 border-t border-domino-cardBorder pt-4 text-sm text-domino-inkSoft">
                <Radar className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Domino surveille ce deal HubSpot jusqu’au{' '}
                  {formatWaitDate(
                    activeHubSpotWait.expiresAt,
                    activeHubSpotWait.timezone
                  )}
                </p>
              </div>
            )}

            {!resultFocused && !canConfirmOutcome && !hasExpandedOptions && <div className="mt-3">{alternativeControl}</div>}
            {!resultFocused && hasExpandedOptions && <details className="group mt-3">
              <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 py-1 text-xs font-medium text-domino-inkSoft transition hover:text-domino-aubergine">
                <ChevronDown className="h-3.5 w-3.5 transition group-open:rotate-180" />
                Ajuster ou reporter
              </summary>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {threadChatDecisionFocusEnabled && topic.decision && (
                  <button
                    type="button"
                    onClick={() => onAskWhy(topic)}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
                  >
                    <CircleHelp className="h-4 w-4" />
                    Pourquoi ?
                  </button>
                )}

                {threadChatDecisionFocusEnabled &&
                  threadWaitTimeEnabled &&
                  topic.decision && (
                    <button
                      type="button"
                      onClick={() => onAskWait(topic)}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
                    >
                      <Clock3 className="h-4 w-4" />
                      Et si j’attends ?
                    </button>
                  )}

                {threadChatDecisionFocusEnabled && topic.decision && (
                  <button
                    type="button"
                    onClick={() => onChallenge(topic)}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
                  >
                    <Scale className="h-4 w-4" />
                    Challenger
                  </button>
                )}

                {!canConfirmOutcome && alternativeControl}

                {threadWaitTimeEnabled && topic.visibleState === 'actionable' && (
                  <button
                    type="button"
                    onClick={openSnooze}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
                  >
                    <Clock3 className="h-4 w-4" />
                    Plus tard
                  </button>
                )}

                {threadWaitGmailEnabled &&
                  topic.visibleState === 'actionable' &&
                  topic.gmailWaitCandidate && (
                    <button
                      type="button"
                      onClick={openGmailMonitor}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                      <Radar className="h-4 w-4" />
                      Surveiller la réponse Gmail
                    </button>
                  )}

                {threadWaitCalendarEnabled &&
                  topic.visibleState === 'actionable' &&
                  topic.calendarWaitCandidate && (
                    <button
                      type="button"
                      onClick={() => openExactProviderMonitor('google_calendar')}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                      <Radar className="h-4 w-4" />
                      Surveiller l’événement Calendar
                    </button>
                  )}

                {threadWaitHubSpotEnabled &&
                  topic.visibleState === 'actionable' &&
                  topic.hubSpotWaitCandidate && (
                    <button
                      type="button"
                      onClick={() => openExactProviderMonitor('hubspot')}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                      <Radar className="h-4 w-4" />
                      Surveiller le deal HubSpot
                    </button>
                  )}

                {threadWaitTimeEnabled && activeTimeWait && (
                  <>
                    <button
                      type="button"
                      onClick={openSnooze}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink disabled:opacity-50"
                    >
                      <Clock3 className="h-4 w-4" />
                      Modifier le rappel
                    </button>
                    <button
                      type="button"
                      onClick={() => void cancelWait()}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center rounded-xl px-3 text-sm font-medium text-domino-inkSoft transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    >
                      Annuler le rappel
                    </button>
                  </>
                )}

                {threadWaitGmailEnabled && activeGmailWait && (
                  <>
                    <button
                      type="button"
                      onClick={openGmailMonitor}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink disabled:opacity-50"
                    >
                      <Clock3 className="h-4 w-4" />
                      Modifier l’échéance
                    </button>
                    <button
                      type="button"
                      onClick={() => void cancelWait()}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center rounded-xl px-3 text-sm font-medium text-domino-inkSoft transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    >
                      Arrêter la surveillance
                    </button>
                  </>
                )}

                {activeProviderWait && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        openExactProviderMonitor(
                          activeProviderWait.providerKind === 'google_calendar'
                            ? 'google_calendar'
                            : 'hubspot'
                        )
                      }
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-inkSoft transition hover:border-domino-prune hover:bg-domino-muted hover:text-domino-ink disabled:opacity-50"
                    >
                      <Clock3 className="h-4 w-4" />
                      Modifier l’échéance
                    </button>
                    <button
                      type="button"
                      onClick={() => void cancelWait()}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center rounded-xl px-3 text-sm font-medium text-domino-inkSoft transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    >
                      Arrêter la surveillance
                    </button>
                  </>
                )}

                {recommendation && recommendation.dismissible !== false && (
                  <>
                    <button
                      type="button"
                      disabled={triagingRecommendationId === recommendation.id}
                      onClick={() =>
                        onTriageRecommendation(recommendation, 'already_done')
                      }
                      className="inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium text-domino-inkSoft transition hover:bg-domino-muted hover:text-domino-ink disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Déjà fait
                    </button>
                    <button
                      type="button"
                      disabled={triagingRecommendationId === recommendation.id}
                      onClick={() =>
                        onTriageRecommendation(recommendation, 'not_relevant')
                      }
                      className="inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium text-domino-inkSoft transition hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    >
                      <XCircle className="h-4 w-4" />
                      Pas pertinent
                    </button>
                  </>
                )}
              </div>
            </details>}

            {waitEditorMode &&
              (waitEditorMode === 'gmail'
                ? threadWaitGmailEnabled
                : waitEditorMode === 'google_calendar'
                  ? threadWaitCalendarEnabled
                  : waitEditorMode === 'hubspot'
                    ? threadWaitHubSpotEnabled
                    : threadWaitTimeEnabled) && (
                <div className="mt-4 rounded-2xl border border-domino-cardBorder bg-domino-warm/80 p-4">
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="min-w-[220px] flex-1 text-xs font-semibold text-domino-ink">
                      {waitEditorMode !== 'time'
                        ? 'Échéance de secours'
                        : 'Date et heure du rappel'}
                      <input
                        type="datetime-local"
                        value={wakeAtInput}
                        min={toLocalDateTimeInput(new Date(Date.now() + 60_000))}
                        onChange={(event) => setWakeAtInput(event.target.value)}
                        disabled={waitMutationPending}
                        className="mt-2 h-10 w-full rounded-xl border border-domino-cardBorder bg-white px-3 text-sm font-normal text-domino-ink outline-none focus:border-domino-prune focus:ring-2 focus:ring-domino-prune/20"
                      />
                    </label>
                    <label className="min-w-[220px] flex-[1.4] text-xs font-semibold text-domino-ink">
                      Motif optionnel
                      <input
                        type="text"
                        maxLength={500}
                        value={snoozeReason}
                        onChange={(event) => setSnoozeReason(event.target.value)}
                        disabled={waitMutationPending}
                        placeholder="Pourquoi revenir sur ce sujet ?"
                        className="mt-2 h-10 w-full rounded-xl border border-domino-cardBorder bg-white px-3 text-sm font-normal text-domino-ink outline-none placeholder:text-domino-inkSoft focus:border-domino-prune focus:ring-2 focus:ring-domino-prune/20"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => void submitWait()}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center rounded-xl bg-domino-aubergine px-4 text-sm font-semibold text-white transition hover:bg-domino-prune disabled:opacity-50"
                    >
                      {waitMutationPending
                        ? 'Programmation…'
                        : waitEditorMode !== 'time'
                          ? 'Confirmer la surveillance'
                          : 'Programmer le rappel'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setWaitEditorMode(null)}
                      disabled={waitMutationPending}
                      className="inline-flex h-10 items-center rounded-xl px-3 text-sm font-medium text-domino-inkSoft hover:bg-domino-muted disabled:opacity-50"
                    >
                      Fermer
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-domino-inkSoft">
                    Fuseau : {timezone}.{' '}
                    {waitEditorMode === 'gmail'
                      ? 'Sans réponse avant cette date, Domino relancera une évaluation sans prétendre avoir reçu un e-mail.'
                      : waitEditorMode === 'google_calendar'
                        ? 'La vérification porte uniquement sur cet événement exact. L’échéance reste un fallback temporel.'
                        : waitEditorMode === 'hubspot'
                          ? 'Seules les propriétés deal allowlistées peuvent réveiller le sujet avant cette échéance.'
                          : 'Le rappel proposé est dans 24 h et reste modifiable.'}
                  </p>
                  {waitMutationError && (
                    <p role="alert" className="mt-2 text-xs font-medium text-red-700">
                      {waitMutationError}
                    </p>
                  )}
                </div>
              )}

            {!waitEditorMode && waitMutationError && (
              <p role="alert" className="mt-3 text-xs font-medium text-red-700">
                {waitMutationError}
              </p>
            )}


          </div>
        )}

        {threadState.otherTopics.length > 0 && (
          <details className="group border-t border-domino-border bg-domino-warm/55 px-6 py-4 md:px-8">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-domino-ink outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
              <span>Autres sujets en cours</span>
              <span className="inline-flex items-center gap-2 text-xs text-domino-inkSoft">
                {threadState.otherTopics.length}
                <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
              </span>
            </summary>
            <ul className="mt-3">
              {threadState.otherTopics.map((otherTopic) => {
                const otherWork = getSubjectWorkActions(detail).find(item => item.id === otherTopic.id);
                return otherWork ? (
                  <li key={otherTopic.id} className="border-t border-domino-border py-4">
                    <WorkActionCard
                      work={otherWork}
                      compact
                      headingAction={<Link to={`?topic=${encodeURIComponent(otherTopic.id)}${accountQuery ? `&q=${encodeURIComponent(accountQuery)}` : ''}`} aria-label={`Ouvrir le travail ${otherWork.title}`} className="shrink-0 text-sm font-semibold text-domino-aubergine underline underline-offset-4">Ouvrir ce travail</Link>}
                    />
                  </li>
                ) : null;
              })}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
};
